"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { HeroGlow } from "@/components/ui";
import { STATUS_URL } from "@/lib/constants";
import { usePrefersLight } from "@/hooks/use-color-scheme";
import { PRIMARY_CARDS, SECONDARY_CARDS } from "./data";
import { PrimaryCard, SecondaryCard } from "./parts";

export default function ContactSection() {
  const prefersReducedMotion = useReducedMotion();
  const prefersLight = usePrefersLight();

  return (
    <main id="main-content" tabIndex={-1} className="overflow-y-clip">
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-20"
        aria-labelledby="contact-heading"
      >
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <EyebrowBadge>Get in touch</EyebrowBadge>
            </motion.div>

            <motion.h1
              id="contact-heading"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              How can we help?
            </motion.h1>

            <motion.p
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-muted-foreground light:text-foreground/80 mx-auto max-w-md text-lg leading-relaxed"
            >
              Get in touch with our sales and support teams for demos, onboarding support, or
              product questions.
            </motion.p>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-16 mb-20 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-3xl gap-5 sm:grid-cols-2">
          {PRIMARY_CARDS.map((card, i) => (
            <PrimaryCard key={card.title} index={i} {...card} />
          ))}
        </div>
      </section>

      <section className="mx-auto mb-20 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-3xl gap-x-12 gap-y-10 sm:grid-cols-2">
          {SECONDARY_CARDS.map((card, i) => (
            <SecondaryCard key={card.title} index={i} {...card} />
          ))}
        </div>
      </section>

      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={scrollRevealViewport}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex justify-center pb-16"
      >
        {/* The badge is a third-party iframe, so its text colour is baked in by
            the `theme` param — it cannot inherit ours. Pinned to dark here, it
            kept rendering white text on the light theme. Same switch the footer
            and /contact/support already use. */}
        <iframe
          src={`${STATUS_URL}/badge?theme=${prefersLight ? "light" : "dark"}`}
          width="190"
          height="30"
          loading="lazy"
          title="Proxidize Status Page - Opens in a new tab"
          aria-label="Proxidize Status Page - Opens in a new tab"
          className="block border-0"
        />
      </motion.div>
    </main>
  );
}
